import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminMessages.css';

const PAGE_SIZE = 20;

function AdminMessages() {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(null); // message shown in detail panel
    const [search, setSearch] = useState('');
    const [filterSubject, setFilterSubject] = useState('All');
    const [filterStatus, setFilterStatus] = useState('All');
    const [page, setPage] = useState(1);
    const [exporting, setExporting] = useState(false);
    const navigate = useNavigate();

    const fetchMessages = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) { navigate('/admin/login'); return; }

        setLoading(true);
        setError('');
        try {
            const apiUrl = process.env.REACT_APP_API_URL || '';
            const res = await fetch(`${apiUrl}/api/messages`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.status === 401) {
                localStorage.removeItem('token');
                navigate('/admin/login');
                return;
            }
            if (!res.ok) throw new Error('Failed to load messages');
            const data = await res.json();
            setMessages(data);
        } catch (err) {
            setError('Could not load messages. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => { fetchMessages(); }, [fetchMessages]);

    // Mark as read when opening detail panel
    const openMessage = async (msg) => {
        setSelected(msg);
        if (!msg.read) {
            const token = localStorage.getItem('token');
            try {
                const apiUrl = process.env.REACT_APP_API_URL || '';
                await fetch(`${apiUrl}/api/messages/${msg.id}/read`, {
                    method: 'PATCH',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, read: true } : m));
                setSelected(prev => prev?.id === msg.id ? { ...prev, read: true } : prev);
            } catch (_) { /* non-critical */ }
        }
    };

    const deleteMessage = async (id) => {
        if (!window.confirm('Delete this message? This cannot be undone.')) return;
        const token = localStorage.getItem('token');
        try {
            const apiUrl = process.env.REACT_APP_API_URL || '';
            const res = await fetch(`${apiUrl}/api/messages/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setMessages(prev => prev.filter(m => m.id !== id));
                if (selected?.id === id) setSelected(null);
            }
        } catch (_) {
            alert('Failed to delete message.');
        }
    };

    const handleExport = async () => {
        setExporting(true);
        const token = localStorage.getItem('token');
        try {
            const apiUrl = process.env.REACT_APP_API_URL || '';
            const res = await fetch(`${apiUrl}/api/messages/export`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Export failed');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const date = new Date().toISOString().split('T')[0];
            a.download = `messages_${date}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (_) {
            alert('Failed to export CSV. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        navigate('/admin/login');
    };

    // Filtering
    const filtered = messages.filter(m => {
        const q = search.toLowerCase();
        const matchSearch = !q || m.name.toLowerCase().includes(q) ||
            m.email.toLowerCase().includes(q) ||
            m.message.toLowerCase().includes(q);
        const matchSubject = filterSubject === 'All' || m.subject === filterSubject;
        const matchStatus = filterStatus === 'All' ||
            (filterStatus === 'Unread' && !m.read) ||
            (filterStatus === 'Read' && m.read);
        return matchSearch && matchSubject && matchStatus;
    });

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const unreadCount = messages.filter(m => !m.read).length;

    const formatDate = (iso) => {
        const d = new Date(iso);
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const truncate = (str, n) => str.length > n ? str.slice(0, n) + '…' : str;

    // Navigate detail panel
    const selectedIdx = selected ? filtered.findIndex(m => m.id === selected.id) : -1;
    const goPrev = () => selectedIdx > 0 && openMessage(filtered[selectedIdx - 1]);
    const goNext = () => selectedIdx < filtered.length - 1 && openMessage(filtered[selectedIdx + 1]);

    return (
        <div className="admin-messages-page">

            {/* Header */}
            <header className="messages-header">
                <div className="messages-header-left">
                    <h1>
                        📬 Messages
                        {unreadCount > 0 && (
                            <span className="unread-badge">{unreadCount}</span>
                        )}
                    </h1>
                    <p className="messages-subtitle">Contact form submissions</p>
                </div>
                <div className="messages-header-right">
                    <a href="/admin" className="header-link">← Content Editor</a>
                    <a href="/admin/media" className="header-link">📁 Media</a>
                    <button onClick={handleLogout} className="logout-btn">Logout</button>
                </div>
            </header>

            <div className="messages-body">

                {/* Toolbar */}
                <div className="messages-toolbar">
                    <div className="toolbar-filters">
                        <input
                            type="text"
                            placeholder="Search name, email, message…"
                            value={search}
                            onChange={e => { setSearch(e.target.value); setPage(1); }}
                            className="messages-search"
                        />
                        <select
                            value={filterSubject}
                            onChange={e => { setFilterSubject(e.target.value); setPage(1); }}
                            className="messages-filter"
                        >
                            <option value="All">All subjects</option>
                            <option value="Project">Project</option>
                            <option value="Career">Career</option>
                            <option value="General">General</option>
                        </select>
                        <select
                            value={filterStatus}
                            onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
                            className="messages-filter"
                        >
                            <option value="All">All status</option>
                            <option value="Unread">Unread</option>
                            <option value="Read">Read</option>
                        </select>
                    </div>
                    <button
                        onClick={handleExport}
                        disabled={exporting || messages.length === 0}
                        className="export-btn"
                        title="Export all messages as CSV"
                    >
                        {exporting ? 'Exporting…' : '⬇ Export CSV'}
                    </button>
                </div>

                {/* Main area */}
                <div className={`messages-main${selected ? ' panel-open' : ''}`}>

                    {/* Table */}
                    <div className="messages-table-wrap">
                        {loading ? (
                            <div className="messages-loading">
                                <div className="msg-spinner"></div>
                                <span>Loading messages…</span>
                            </div>
                        ) : error ? (
                            <div className="messages-error" role="alert">
                                <p>{error}</p>
                                <button onClick={fetchMessages} className="retry-btn">Retry</button>
                            </div>
                        ) : filtered.length === 0 ? (
                            <div className="messages-empty">
                                <div className="empty-icon">📭</div>
                                <h3>{messages.length === 0 ? 'No messages yet' : 'No results'}</h3>
                                <p>
                                    {messages.length === 0
                                        ? 'When visitors submit the contact form, their messages will appear here.'
                                        : 'Try adjusting your search or filters.'}
                                </p>
                                {messages.length === 0 && (
                                    <a href="/contact" target="_blank" rel="noreferrer" className="view-form-link">
                                        View contact form →
                                    </a>
                                )}
                            </div>
                        ) : (
                            <>
                                <table className="messages-table">
                                    <thead>
                                        <tr>
                                            <th className="col-status" aria-label="Read status"></th>
                                            <th className="col-name">Name</th>
                                            <th className="col-subject">Subject</th>
                                            <th className="col-preview">Message</th>
                                            <th className="col-date">Date</th>
                                            <th className="col-email">Email</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginated.map(msg => (
                                            <tr
                                                key={msg.id}
                                                className={`msg-row${!msg.read ? ' unread' : ''}${selected?.id === msg.id ? ' active' : ''}`}
                                                onClick={() => openMessage(msg)}
                                                tabIndex={0}
                                                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && openMessage(msg)}
                                                aria-label={`Message from ${msg.name}`}
                                            >
                                                <td className="col-status">
                                                    {!msg.read && <span className="unread-dot" aria-label="Unread"></span>}
                                                </td>
                                                <td className="col-name">{msg.name}</td>
                                                <td className="col-subject">
                                                    <span className={`subject-badge subject-${msg.subject.toLowerCase()}`}>
                                                        {msg.subject}
                                                    </span>
                                                </td>
                                                <td className="col-preview">{truncate(msg.message, 80)}</td>
                                                <td className="col-date">{formatDate(msg.submittedAt)}</td>
                                                <td className="col-email">{msg.email}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="messages-pagination">
                                        <button
                                            onClick={() => setPage(p => Math.max(1, p - 1))}
                                            disabled={page === 1}
                                            className="page-btn"
                                        >
                                            ← Prev
                                        </button>
                                        <span className="page-info">
                                            Page {page} of {totalPages} ({filtered.length} messages)
                                        </span>
                                        <button
                                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                            disabled={page === totalPages}
                                            className="page-btn"
                                        >
                                            Next →
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Detail panel */}
                    {selected && (
                        <aside className="message-detail-panel">
                            <div className="detail-header">
                                <div className="detail-nav">
                                    <button
                                        onClick={goPrev}
                                        disabled={selectedIdx <= 0}
                                        className="nav-arrow"
                                        title="Previous message"
                                    >
                                        ↑
                                    </button>
                                    <button
                                        onClick={goNext}
                                        disabled={selectedIdx >= filtered.length - 1}
                                        className="nav-arrow"
                                        title="Next message"
                                    >
                                        ↓
                                    </button>
                                </div>
                                <button
                                    onClick={() => setSelected(null)}
                                    className="close-panel"
                                    aria-label="Close detail panel"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="detail-body">
                                <div className="detail-meta">
                                    <h2 className="detail-name">{selected.name}</h2>
                                    <span className={`subject-badge subject-${selected.subject.toLowerCase()}`}>
                                        {selected.subject}
                                    </span>
                                </div>

                                <div className="detail-info">
                                    <p><strong>Email:</strong> <a href={`mailto:${selected.email}`}>{selected.email}</a></p>
                                    {selected.phone && <p><strong>Phone:</strong> <a href={`tel:${selected.phone}`}>{selected.phone}</a></p>}
                                    <p><strong>Date:</strong> {new Date(selected.submittedAt).toLocaleString()}</p>
                                    <p><strong>Status:</strong> {selected.read ? '✓ Read' : '● Unread'}</p>
                                </div>

                                <div className="detail-message">
                                    <h4>Message</h4>
                                    <p>{selected.message}</p>
                                </div>

                                <div className="detail-actions">
                                    <a
                                        href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`}
                                        className="reply-btn"
                                    >
                                        ✉ Reply
                                    </a>
                                    <button
                                        onClick={() => deleteMessage(selected.id)}
                                        className="delete-btn"
                                    >
                                        🗑 Delete
                                    </button>
                                </div>
                            </div>
                        </aside>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AdminMessages;
